/**
 * Planning a new activity — from the activities screen, or from a deal's,
 * person's or organization's panel with `link` pre-filled.
 *
 * Owns its form and its request; the caller opens it and hears `onCreated`.
 */
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { ActivityDto } from '@forsys/shared';
import { FormDialog } from '../../components/FormDialog';
import { useCrudDialogs } from '../../hooks/useCrudDialogs';
import { useSubmit } from '../../hooks/useSubmit';
import { createActivity } from './api';
import {
  ActivityFields,
  activityIncomplete,
  blankActivity,
  createActivityRequest,
  type ActivityForm,
} from './forms';

export interface NewActivityDialogProps {
  open: boolean;
  /** What the new activity is linked to from the start. */
  link?: Parameters<typeof blankActivity>[0];
  onClose: () => void;
  onCreated: (activity: ActivityDto) => void;
}

export function NewActivityDialog({ open, link = {}, onClose, onCreated }: NewActivityDialogProps) {
  const { t } = useTranslation('crm');
  const crud = useCrudDialogs<ActivityDto, ActivityForm>({
    blank: blankActivity(link),
    formFor: () => blankActivity(link),
  });

  // A fresh form every time it opens, starting from today and the link.
  const { openCreate, close } = crud;
  useEffect(() => {
    if (open) openCreate();
    else close();
  }, [open, openCreate, close]);

  const create = useSubmit(async () => {
    const { activity } = await createActivity(createActivityRequest(crud.form));
    onCreated(activity);
    return activity;
  });

  return (
    <FormDialog
      open={open}
      title={t('activity.createDialog.title')}
      submitLabel={t('activity.createDialog.submit')}
      pending={create.pending}
      error={create.error}
      disabled={activityIncomplete(crud.form)}
      onClose={onClose}
      onSubmit={async () => {
        if (await create.submit()) onClose();
      }}
    >
      <ActivityFields form={crud.form} patch={crud.patch} />
    </FormDialog>
  );
}
